import { DirectiveVersionHistory } from "@/components/directive/DirectiveVersionHistory";

const PRINCIPLES = [
  {
    number: 1,
    title: "Consent is the subject's alone to give.",
    body: "No one — not family, not purchaser, not company — can consent to a person's digital legacy on their behalf. The subject is the only valid authorizer.",
  },
  {
    number: 2,
    title: "Consent requires comprehension.",
    body: "A subject must understand what is being created, who will have access, and what can happen to it during and beyond their life, before consent is recorded.",
  },
  {
    number: 3,
    title: "Consent is verifiable.",
    body: "Every consent record includes an audio or video recording of the subject verbally stating their consent — cryptographically bound to the moment of authorization — so it cannot be disputed or fabricated.",
  },
  {
    number: 4,
    title: "Consent is revocable.",
    body: "A subject may revoke consent at any time during life, and designated executors may act on a subject's previously stated wishes after death.",
  },
  {
    number: 5,
    title: "Scope is explicit.",
    body: "What is authorized is defined in the consent statement at the time of consent — likeness, voice, content, duration, audience — and cannot be expanded without new consent.",
  },
  {
    number: 6,
    title: "Data belongs to the subject.",
    body: "The subject (and their estate) retain ownership of all captured material. The platform holds it in trust, not in possession.",
  },
  {
    number: 7,
    title: "No exploitation for commercial purposes outside the legacy's intent.",
    body: "A digital legacy created under the Directive may not be used for advertising, to train commercial AI models, or for any purpose beyond honoring the subject and fulfilling the authorized intent. Modest access fees that fund preservation, compensate stakeholders, and support related causes are permitted.",
  },
  {
    number: 8,
    title: "Transparent preservation in perpetuity.",
    body: "The platform publishes how it stores, protects, and backs up consent records and legacy content. Digital legacies are committed to preservation in perpetuity, subject to the portability guarantees in Principle 10.",
  },
  {
    number: 9,
    title: "Public by consent, not by default.",
    body: "A digital legacy is shared only according to the subject's explicit authorization. What is captured is what the subject chose to record; what is shared is what the subject chose to share.",
  },
  {
    number: 10,
    title: "Portability in platform transition.",
    body: "If the platform ceases operations, subjects (or the estates of deceased subjects) will be given a minimum of 180 days' advance notice before any content or consent records are permanently removed, and a minimum of 365 days from the date of notice during which they may retrieve their digital legacies in a portable, usable format. If the platform is acquired, the acquiring entity must either publicly commit to honoring this Directive or provide the same notice and retrieval guarantees.",
  },
];

const principleId = (n: number) => `principle-${n}`;

export default function DigitalLegacyDirective() {
  return (
    <main className="mx-auto max-w-3xl px-6 py-16 md:py-24 text-foreground">
      {/* Header */}
      <header className="mb-12 border-b border-border pb-8">
        <h1 className="text-4xl md:text-5xl font-semibold tracking-tight mb-3">
          The Digital Legacy Directive
        </h1>
        <p className="text-base text-muted-foreground">
          Version 1.0 — April 24, 2026
        </p>
      </header>

      {/* Preamble */}
      <section className="mb-16">
        <h2 className="text-2xl md:text-3xl font-semibold tracking-tight mb-6">
          Preamble
        </h2>
        <div className="space-y-5 text-lg leading-relaxed">
          <p>
            A person's voice, likeness, and stories are theirs alone. When we
            preserve these things so they can outlast us, or so they can be
            shared beyond the moment of our lives, we are creating something
            powerful — something families will treasure, and something that,
            without care, could be misused.
          </p>
          <p>
            The Digital Legacy Directive is a framework for doing this right.
            It defines how a person's digital legacy — their voice, likeness,
            memories, and words — may be captured, preserved, and shared, with
            their full understanding and unambiguous consent.
          </p>
          <p>
            This Directive applies to any product or service that creates a
            persistent digital representation of a real person with their
            authorization — including memorials of the deceased, legacy
            archives recorded during life for future generations, oral
            histories, and any similar effort that preserves a person's voice,
            likeness, or memories beyond the ordinary reach of their lifetime.
          </p>
          <p>
            The Directive exists because the technology to preserve a person's
            likeness and voice indefinitely is now real, and because most of
            the products doing this work have not yet answered the hardest
            questions: Who has the right to authorize a digital legacy? How do
            we prove consent was genuine? What happens to that legacy after
            the person is gone, or after the company that created it
            disappears?
          </p>
          <p>
            The Directive is our answer. It is also our commitment. Feel free
            to utilize and share it for free.
          </p>
        </div>
      </section>

      {/* The Ten Principles */}
      <section className="mb-16">
        <h2 className="text-2xl md:text-3xl font-semibold tracking-tight mb-8">
          The Ten Principles
        </h2>
        <ol className="space-y-8 list-none pl-0">
          {PRINCIPLES.map((p) => (
            <li
              key={p.number}
              id={principleId(p.number)}
              className="scroll-mt-24"
            >
              <h3 className="text-xl font-semibold mb-2 leading-snug">
                {p.number}. {p.title}
              </h3>
              <p className="text-lg leading-relaxed text-muted-foreground">
                {p.body}
              </p>
            </li>
          ))}
        </ol>
      </section>

      {/* What This Means in Practice */}
      <section className="mb-16">
        <h2 className="text-2xl md:text-3xl font-semibold tracking-tight mb-6">
          What This Means in Practice
        </h2>
        <div className="space-y-5 text-lg leading-relaxed">
          <p>
            These ten principles are not aspirational. Each one maps to how
            LegacyLens AI already operates, and each one is intended to be
            adoptable by any product or service in the digital legacy space.
          </p>
          <p>
            <a href={`#${principleId(1)}`} className="font-semibold underline-offset-4 hover:underline">
              Principles 1 and 2
            </a>{" "}
            — subject-only consent with comprehension — shape every capture
            session. A buyer may commission a digital legacy as a gift, but
            the subject themselves must walk through the consent process,
            understand what is being created, and authorize it before any
            data is retained. No legacy is created without the subject's
            personal involvement.
          </p>
          <p>
            <a href={`#${principleId(3)}`} className="font-semibold underline-offset-4 hover:underline">
              Principle 3
            </a>{" "}
            — verifiable consent — is the reason every consent record we
            store includes an audio or video recording of the subject
            reading a consent statement aloud. That recording is
            authenticated against the voice or likeness used in the legacy
            itself. If anyone ever challenges whether consent was real, the
            answer is in the subject's own spoken words, at the exact
            moment of authorization.
          </p>
          <p>
            <a href={`#${principleId(4)}`} className="font-semibold underline-offset-4 hover:underline">
              Principles 4 and 5
            </a>{" "}
            — revocation and explicit scope — are written into every consent
            record with version-locked terms. The scope of what is
            authorized is stated clearly in the consent statement the
            subject reads at the time of recording. A subject revoking
            consent during their lifetime triggers immediate suspension of
            their digital legacy. After death, executors may act only
            within the scope the subject authorized in life.
          </p>
          <p>
            <a href={`#${principleId(6)}`} className="font-semibold underline-offset-4 hover:underline">
              Principles 6 and 7
            </a>{" "}
            — ownership and no commercial exploitation — are binding
            contractual terms. LegacyLens does not train AI models on
            subject data, does not license legacies for advertising, and
            does not treat a subject's voice or likeness as a platform
            asset. A modest access fee may be charged to fund preservation,
            return value to the family or gifter who commissioned the
            legacy, sustain partner institutions hosting the tribute (such
            as funeral homes or cemeteries), and support related causes; no
            legacy is ever sold or monetized for purposes outside its
            intent.
          </p>
          <p>
            <a href={`#${principleId(8)}`} className="font-semibold underline-offset-4 hover:underline">
              Principle 8
            </a>{" "}
            — transparent preservation in perpetuity — means that every
            element of a digital legacy, including consent records, 3D
            likenesses, voice recordings, voice clones, and memory
            archives, is preserved with architectural redundancy across
            independent systems, without expiration. We publish our
            current preservation architecture on our website and update it
            as infrastructure evolves. Digital legacies are designed to
            last forever.
          </p>
          <p>
            <a href={`#${principleId(9)}`} className="font-semibold underline-offset-4 hover:underline">
              Principle 9
            </a>{" "}
            — public by consent — reflects how our product actually works.
            A subject who chooses to create a legacy chooses how it will
            be shared. For LegacyLens, that typically means
            voice, likeness, and recorded memories become publicly
            accessible so that family members, descendants, and visitors
            can encounter them. Subjects are informed of
            this before recording begins, and reminded throughout the
            interview process. If there is something a subject does not
            want the world to know, they are instructed not to record it.
            The content of the legacy is whatever the subject decides to
            leave behind.
          </p>
          <p>
            <a href={`#${principleId(10)}`} className="font-semibold underline-offset-4 hover:underline">
              Principle 10
            </a>{" "}
            — portability in platform transition — is our deepest
            commitment. A consent framework that disappears when the
            company disappears is not a real framework. We commit, in our
            Terms of Service and in this public document, that if
            LegacyLens ceases operations, subjects and their estates will
            receive 180 days' notice and a 365-day retrieval window before
            any data is permanently removed. If LegacyLens is acquired,
            the acquiring entity must either publicly assume the
            obligations of this Directive or provide the same notice and
            retrieval guarantees.
          </p>
          <p>
            LegacyLens is also actively establishing a perpetual
            preservation trust — modeled on the legally-regulated
            perpetual care funds that sustain physical cemeteries — to
            ensure that tributes commissioned through our platform
            remain preserved even in the event of the company's
            dissolution. Details of the trust structure will be published
            as it is finalized.
          </p>
        </div>
      </section>

      {/* Relationship to Law */}
      <section className="mb-16">
        <h2 className="text-2xl md:text-3xl font-semibold tracking-tight mb-6">
          Relationship to Law
        </h2>
        <div className="space-y-5 text-lg leading-relaxed">
          <p>
            This Directive is a voluntary standard. It does not replace or
            supersede applicable law.
          </p>
          <p>
            In jurisdictions with specific statutory protections for voice,
            likeness, digital persona, or post-mortem rights — including
            California's right of publicity after death, Tennessee's ELVIS
            Act, New York's right of publicity law, and similar statutes in
            other states and countries — those laws control wherever they
            offer greater protection than this Directive. Where applicable
            law is silent, this Directive applies.
          </p>
          <p>
            Companies and products adopting this Directive are responsible
            for their own compliance with the laws of the jurisdictions in
            which they operate.
          </p>
        </div>
      </section>

      {/* Versioning and Change */}
      <section className="mb-16">
        <h2 className="text-2xl md:text-3xl font-semibold tracking-tight mb-6">
          Versioning and Change
        </h2>
        <div className="space-y-5 text-lg leading-relaxed">
          <p>
            This Directive is versioned. The current version is{" "}
            <strong>1.0</strong>, effective April 24, 2026.
          </p>
          <p>
            Every consent record created under this Directive stores the
            version number in effect at the moment of authorization. If
            this Directive is revised, previously-recorded consent remains
            governed by the version the subject agreed to. Subjects retain
            the right to re-consent under a newer version, or to withdraw
            consent if a new version no longer reflects their wishes.
          </p>
          <p>
            Changes to this Directive will be published publicly with a
            summary of what changed and why, at this same URL, with prior
            versions archived and accessible.
          </p>
        </div>

        <DirectiveVersionHistory />
      </section>

      {/* Why This Is Published */}
      <section className="mb-16">
        <h2 className="text-2xl md:text-3xl font-semibold tracking-tight mb-6">
          Why This Is Published
        </h2>
        <div className="space-y-5 text-lg leading-relaxed">
          <p>
            The Digital Legacy Directive is not a LegacyLens product. It is
            a standard that LegacyLens authored, follows, and makes
            available for others to follow.
          </p>
          <p>
            Any company, platform, or product working in the space of
            digital legacy — including memorial services, oral history
            projects, legacy archives, voice and likeness preservation,
            and post-mortem AI representation — is welcome to adopt the
            Directive as written, or to extend it. We believe this work
            should not be done without a standard, and we believe a
            standard authored by people who take consent seriously is
            better than no standard at all.
          </p>
          <p>
            If you are building in this space and want to adopt the
            Directive, no permission is needed. Use it. Reference it by
            name. Improve it where you can.
          </p>
          <p>
            Organizations and products adopting this Directive may
            publicly declare their compliance. Over time, LegacyLens may
            offer verification and certification programs to distinguish
            attested from audited compliance.
          </p>
        </div>
      </section>

      {/* Footer / colophon */}
      <footer className="border-t border-border pt-8 text-base italic text-muted-foreground leading-relaxed">
        <p>
          The Digital Legacy Directive v1.0 — authored and maintained by
          LegacyLens AI, Inc. This document is published at{" "}
          <span className="not-italic">legacylensai.com/digital-legacy-directive</span>{" "}
          and is freely adoptable by others. For questions, correspondence,
          or proposed revisions, contact{" "}
          <a
            href="mailto:directive@legacylensai.com"
            className="not-italic font-medium underline-offset-4 hover:underline"
          >
            directive@legacylensai.com
          </a>
          .
        </p>
      </footer>
    </main>
  );
}
